const BattleCalc = require('../BattleCalc');
const Balance = require('../../constants/Balance');

const AICalc = {
  SUPPORT_DECISION_LOW_HP_THRESHOLD: 80, // percent

  getBestMeleeTarget: function (actor, battleState) {
    let candidates = battleState.getEnemiesOfUnit(actor);
    if (!Array.isArray(candidates) && typeof candidates === 'object') {
      candidates = Object.values(candidates);
    }

    // heroes only target heroes in PvP
    if (!battleState.isCaverns && actor.hero && !actor.poppet) {
      candidates = candidates.filter(({ hero, poppet }) => hero && !poppet);
    }
    if (!candidates?.length) return null;

    let bestTarget = candidates[0];
    let bestDist = BattleCalc.boardDistance(actor, bestTarget);

    for (const enemy of candidates) {
      if (!enemy.inPlay) {
        continue;
      }

      const dist = BattleCalc.boardDistance(actor, enemy);

      if (
        dist < bestDist ||
        (dist === bestDist &&
          AICalc.getEffectiveHealthRemaining({
            attacker: actor,
            defender: enemy,
          }) <
            AICalc.getEffectiveHealthRemaining({
              attacker: actor,
              defender: bestTarget,
            }))
      ) {
        bestTarget = enemy;
        bestDist = dist;
      }
    }

    return bestTarget;
  },

  getBestMissileTarget: function (actor, battleState) {
    let candidates = battleState.getEnemiesOfUnit(actor);
    if (!Array.isArray(candidates) && typeof candidates === 'object') {
      candidates = Object.values(candidates);
    }

    // heroes only target heroes in PvP
    if (!battleState.isCaverns && actor.hero && !actor.poppet) {
      candidates = candidates.filter(({ hero, poppet }) => hero && !poppet);
    }

    if (!candidates?.length) {
      return null;
    }

    // figure out what my lowest range ability is
    let least_range = 50;
    for (const ability_handle of [
      'arrow_shot',
      'razor_shot',
      'void_spike',
      'infect_mind',
      'molotov_cocktail',
      'multi_shot',
      'fumigate',
      'lightning',
      'chain_pull',
    ]) {
      const ability = BattleCalc.getEquippedAbility(actor, ability_handle);
      if (ability && ability.range.max < least_range) {
        least_range = ability.range.max;
      }
    }

    return getLowestHealthCandidateInRange({ range: least_range, candidates });

    function getLowestHealthCandidateInRange({ range, candidates }) {
      let lowest_health = Infinity;
      let result;
      // find candidate lowest health within range (if nx, increment range)
      for (let r = 1; r <= range; r++) {
        for (const candidate of candidates) {
          if (BattleCalc.boardDistance(actor, candidate) <= r) {
            const health = AICalc.getEffectiveHealthRemaining({
              attacker: actor,
              defender: candidate,
            });
            if (health < lowest_health) {
              lowest_health = health;
              result = candidate;
            }
          }
        }
      }

      return result
        ? result
        : range < 25
          ? getLowestHealthCandidateInRange({ range: range + 1, candidates })
          : AICalc.getBestMeleeTarget(actor, battleState);
    }
  },

  getBestDoTTarget(actor, initial_target, battleState) {
    let dot_ability_dist = 0;
    for (const ability_handle of ['razor_shot', 'infect_mind', 'fumigate']) {
      const ability = BattleCalc.getEquippedAbility(actor, ability_handle);
      if (ability && ability.range.max > dot_ability_dist) {
        dot_ability_dist = ability.range.max;
        break;
      }
    }

    // in Caverns, spread DoTs around to maximize AoE damage
    if (battleState.isCaverns) {
      let least_potency = BattleCalc.getCombinedDoTPotency(initial_target);
      let least_dotted_unit = initial_target;

      const allies_of_target = BattleCalc.getAlliesOfUnit(
        initial_target,
        battleState.allUnits,
        true
      );

      for (const ally of allies_of_target) {
        const combined_potency = BattleCalc.getCombinedDoTPotency(ally);
        if (
          combined_potency < least_potency &&
          BattleCalc.boardDistance(ally, actor) <= dot_ability_dist
        ) {
          least_potency = combined_potency;
          least_dotted_unit = ally;
        }
      }

      return least_dotted_unit;
    } else {
      // in PvP, focus one target with DoTs
      return AICalc.getBestMissileTarget(actor, battleState);
    }
  },

  defineEnemyCluster: function (target, candidates) {
    if (!Array.isArray(candidates) && typeof candidates === 'object') {
      candidates = Object.values(candidates);
    }
    return candidates.filter((enemy) => BattleCalc.boardDistance(enemy, target) < 3);
  },

  getNearbyUnits: function (subject, candidates, defineNearby) {
    var nearbyUnits = [];
    for (var i = 0; i < candidates.length; ++i) {
      var unit = candidates[i];
      if (BattleCalc.boardDistance(subject, unit) <= defineNearby) {
        nearbyUnits.push(unit);
      }
    }

    return nearbyUnits;
  },

  getBestSummonTile(actor, battleState) {
    var tile = null;
    var candidateTiles = [];
    var attempts = 0;

    var distFromActor = 1;
    do {
      for (var tid in battleState.allTiles) {
        var tile = battleState.allTiles[tid];
        if (BattleCalc.boardDistance(tile, actor) <= distFromActor && !tile.occupied) {
          candidateTiles.push(tile);
        }
      }
      ++distFromActor;
    } while (
      distFromActor <= Balance.CONJURATION_RANGE &&
      candidateTiles.length === 0 &&
      ++attempts < 50
    );

    if (candidateTiles.length === 0) {
      return null;
    } else {
      return candidateTiles[Math.floor(Math.random() * candidateTiles.length)];
    }
  },

  getEffectiveHealthRemaining({ attacker, defender }) {
    // (defender_hp / ( stance_mod * armor_mod * affinity_mod )
    return (
      defender.hp /
      (BattleCalc.getDamageModFromStances({
        instigator: attacker,
        victim: defender,
      }) *
        (1 - Balance.getDmgReduxFromArmorClass(defender.attributes.armorClass)) *
        BattleCalc.getDamageModFromAffinities(attacker, defender))
    );
  },
};
module.exports = AICalc;
